/**
 * Background Gallery Component
 * Shows preset and custom backgrounds for virtual background selection
 */

import { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useVideoEffectsStore } from '../../contexts/videoEffectsStore';

export default function BackgroundGallery() {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const {
    presetBackgrounds,
    customBackgrounds,
    selectedBackground,
    setSelectedBackground,
    setEffectType,
    addCustomBackground,
    removeCustomBackground,
  } = useVideoEffectsStore();

  const handleSelectBackground = (url) => {
    setSelectedBackground(url);
    setEffectType('background');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input
    event.target.value = '';

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      // Convert file to data URL (base64) so it persists after page reload
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // Validate it's a valid image by loading it
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = () => reject(new Error('Invalid image'));
        img.src = dataUrl;
      });

      addCustomBackground(dataUrl);
      handleSelectBackground(dataUrl);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCustom = (e, bg) => {
    e.stopPropagation();
    removeCustomBackground(bg.id);
  };

  const allBackgrounds = [...presetBackgrounds, ...customBackgrounds];

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        Choose a Background
      </Typography>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={1}>
        {allBackgrounds.map((bg) => (
          <Grid item xs={4} key={bg.id}>
            <Card
              sx={{
                cursor: 'pointer',
                position: 'relative',
                border: selectedBackground === bg.url ? '3px solid' : '1px solid',
                borderColor: selectedBackground === bg.url ? 'primary.main' : 'divider',
                borderRadius: 1,
                transition: 'border-color 0.2s, transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                  borderColor: selectedBackground === bg.url ? 'primary.main' : 'primary.light',
                },
              }}
              onClick={() => handleSelectBackground(bg.url)}
            >
              <CardMedia
                component="img"
                height="70"
                image={bg.url}
                alt={bg.name}
                sx={{ objectFit: 'cover' }}
                onError={(e) => {
                  // Show placeholder on error
                  e.target.src =
                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="70"%3E%3Crect fill="%23ccc" width="100" height="70"/%3E%3C/svg%3E';
                }}
              />
              {selectedBackground === bg.url && (
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: 'primary.main',
                    bgcolor: 'white',
                    borderRadius: '50%',
                    fontSize: 20,
                  }}
                />
              )}
              {bg.id.startsWith('custom-') && (
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      p: 0.3,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    }}
                    onClick={(e) => handleRemoveCustom(e, bg)}
                  >
                    <DeleteIcon sx={{ color: 'white', fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Card>
          </Grid>
        ))}

        {/* Upload button */}
        <Grid item xs={4}>
          <Card
            sx={{
              height: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: uploading ? 'wait' : 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {uploading ? (
              <CircularProgress size={24} />
            ) : (
              <Tooltip title="Upload image">
                <AddPhotoAlternateIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
              </Tooltip>
            )}
          </Card>
        </Grid>
      </Grid>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Supported formats: JPG, PNG, WebP (max 5MB)
      </Typography>
    </Box>
  );
}
